import React from "react";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { EditButton, DeleteButton } from "@refinedev/mui";

import { TableCell, ActiveCompanySwitch } from "@shared/components";
import { Agreement } from "@modules/agreements/types";
import { Button } from "@mui/material";
import { api } from "@api/api";

const statusOptions: any = {
  "pending-approval": "Aguardando aprovação",
  approved: "Aprovado",
  "pending-training": "Aguardando treinamento",
};

export const useCompaniesListColumns = (
  onRefetch: () => void,
  translate: (key: string) => string,
  agreements?: Agreement[]
): GridColDef[] => {
  const handleApproveCompany = async ({
    id,
    status,
  }: {
    id: number;
    status: string;
  }) => {

    await api.patch(`v1/company_admin/${id}/`, {
      status,
    });

    onRefetch();
  };

  const renderApproveCompany = (params: GridRenderCellParams) => {
    const needsApproval =
      ["pending-approval", "pending-training"].indexOf(params.row.status) !==
      -1;
    const isPendingApproval = params.row.status === "pending-approval";
    const nextStage = isPendingApproval ? "pending-terms" : "approved";
    const buttonText = isPendingApproval
      ? "Aprovar Dados"
      : "Aprovar Treinameno";

    if (needsApproval) {
      return (
        <Button
          variant="outlined"
          color="primary"
          size="small"
          sx={{
            textTransform: "none",
            width: "100px",
            flexGrow: 1,
            whiteSpace: "normal",
            lineHeight: 1.2,
          }}
          onClick={() =>
            handleApproveCompany({
              id: params.row.id,
              status: nextStage,
            })
          }
        >
          {buttonText.split(" ").map((word, index) => (
            <React.Fragment key={index}>
              {word}
              {index < buttonText.split(" ").length - 1 && <br />}
            </React.Fragment>
          ))}
        </Button>
      );
    }

    return null;
  };

  return React.useMemo<GridColDef[]>(
    () => [
      {
        field: "name",
        headerName: translate("v1/company_admin.fields.name"),
        minWidth: 250,
        headerAlign: "left",
        renderCell: ({ value }) => (
          <TableCell.Text value={value} textAlign="left" />
        ),
      },
      {
        field: "cnpj",
        headerName: translate("v1/company_admin.fields.cnpj"),
        minWidth: 160,
        headerAlign: "left",
        renderCell: ({ value }) => (
          <TableCell.Text value={value} textAlign="left" />
        ),
      },
      {
        field: "category",
        headerName: translate("v1/company_admin.fields.category"),
        minWidth: 120,
        headerAlign: "left",
        renderCell: ({ value }) => <TableCell.Text value={value} />,
      },
      {
        field: "agreement",
        headerName: translate("v1/company_admin.fields.agreement"),
        minWidth: 120,
        headerAlign: "left",
        renderCell: ({ value }) => (
          <TableCell.Link
            to={`/agreements/edit/${value}`}
            value={
              agreements?.find((agreement) => agreement.id === value)?.name
            }
          />
        ),
      },
      {
        field: "creator_origin",
        headerName: translate("v1/company_admin.fields.creator_origin"),
        minWidth: 120,
        headerAlign: "left",
        renderCell: ({ value }) => (
          <TableCell.Text value={value} textAlign="left" />
        ),
      },
      {
        field: "status",
        headerName: translate("v1/company_admin.fields.status"),
        minWidth: 150,
        headerAlign: "left",
        renderCell: ({ value }) => (
          <TableCell.Text
            value={statusOptions[value] || value}
            textAlign="left"
          />
        ),
      },
      {
        field: "active",
        headerName: translate("v1/company_admin.fields.active"),
        minWidth: 120,
        headerAlign: "center",
        renderCell: ({ value, row }) => (
          <TableCell.Container align="center">
            <ActiveCompanySwitch value={value} id={row.id} />
          </TableCell.Container>
        ),
      },
      {
        field: "created_at",
        headerName: translate("v1/company_admin.fields.created_at"),
        minWidth: 120,
        headerAlign: "center",
        renderCell: ({ value }) => <TableCell.Date value={value} />,
      },
      {
        field: "aprove_company",
        headerName: "",
        minWidth: 120,
        headerAlign: "left",
        renderCell: renderApproveCompany,
      },
      {
        field: "actions",
        headerName: translate("table.actions"),
        display: "flex",
        headerAlign: "center",
        minWidth: 120,
        sortable: false,
        renderCell: ({ row }) => (
          <TableCell.Container align="center">
            <EditButton hideText recordItemId={row.id} />
            <DeleteButton
              hideText
              recordItemId={row.id}
              onSuccess={onRefetch}
            />
          </TableCell.Container>
        ),
      },
    ],
    [translate, onRefetch, agreements]
  );
};
